/**
 * src/shell/hex_backdrop.js
 *
 * The interactive "hex-grid" identity texture (DESIGN_SPEC §1.1). CK-flagged
 * defect (2026-07-15): tokens.css already carried a CSS-only hex lattice
 * (six overlaid 1px hairline gradients at ~0.045 alpha), but it was (a)
 * imperceptibly faint on a near-black surface and (b) entirely static — no
 * interactivity, unlike the Flutter mobile app's HexWallpaper
 * (lib/core/widgets/hex_wallpaper.dart), which DOES track the pointer with a
 * soft glow. This module gives web-admin the same interactive behaviour:
 *
 *   - a literal hexagon lattice (not hairlines) drawn on a single full-
 *     viewport <canvas>, tinted per the active theme (emerald on dark,
 *     faint rose on light — mirrors tokens.css's palette so it reads as one
 *     system, not a bolt-on effect)
 *   - a soft radial glow that follows the pointer, brightening nearby hexes
 *     (redblobgames-style pointy-top hex math) — smoothed (lerped) frame to
 *     frame, exactly like the mobile widget's _smoothPointer
 *   - idles out: the animation loop stops entirely ~900ms after the last
 *     pointer move (fading the glow to nothing first) rather than running
 *     forever at 60fps, and is fully paused whenever the tab is hidden
 *     (visibilitychange) — "cheap, no layout thrash, throttled"
 *   - honors prefers-reduced-motion: reduce → a single static paint, no
 *     pointer listener, no rAF loop, ever
 *
 * Performance: the static lattice is pre-rendered ONCE (on mount / resize /
 * theme flip) into an offscreen canvas; every animated frame is a single
 * drawImage() blit of that cached lattice plus a small, distance-bounded
 * re-stroke of only the ~100-150 hexes within the glow radius — never a
 * full-grid re-stroke per frame.
 *
 * mountHexBackdrop() is a module-level singleton — the canvas is appended
 * once as a fixed sibling of #app directly under <body> (so a shell/login
 * remount, which only ever replaces #app's innerHTML, never disturbs it),
 * and every listener is attached exactly once no matter how many of
 * login.js / shell.js / employee_shell.js call it. z-index:-1 keeps it
 * behind #app's entire (non-positioned) content without requiring any other
 * element in the app to manage z-index around it.
 */

const CANVAS_ID = 'hex-backdrop-canvas';

const HEX_R = 34;              // center-to-vertex radius, CSS px
const IDLE_FADE_MS = 900;      // glow fade-out duration after the last pointer move
const GLOW_RADIUS = 230;       // px — how far the cursor's brightening reaches
const POINTER_LERP = 0.18;     // pointer smoothing factor per frame (mirrors mobile _smoothPointer)
const MAX_DPR = 2;             // cap devicePixelRatio scaling cost on high-DPI panels

// Theme-scoped literal tints (mirrors tokens.css's existing convention of
// literal rgba inside a theme-scoped selector — see tokens.css §"Background
// texture" comment): emerald on dark (matches --accent), a much fainter rose
// on light (matches the light theme's --accent, kept quiet so it never
// muddies the light surface).
const DARK_LINE = [46, 139, 115];
const DARK_BASE_ALPHA = 0.11;
const DARK_GLOW_ALPHA = 0.5;
const LIGHT_LINE = [219, 39, 119];
const LIGHT_BASE_ALPHA = 0.05;
const LIGHT_GLOW_ALPHA = 0.26;

let inited = false;
let canvas = null;
let ctx = null;
let baseCanvas = null;
let baseCtx = null;
let dpr = 1;
let w = 0;
let h = 0;
let reduceMotion = false;
let hexGeom = null; // { hexW, hexH, vStep }

let pointer = null;       // smoothed {x,y}
let targetPointer = null; // raw latest {x,y}
let lastMoveAt = 0;
let rafId = null;

export function mountHexBackdrop() {
  if (inited) return;
  inited = true;

  canvas = document.getElementById(CANVAS_ID);
  if (!canvas) {
    canvas = document.createElement('canvas');
    canvas.id = CANVAS_ID;
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText = 'position:fixed;inset:0;z-index:-1;pointer-events:none;display:block';
    document.body.insertBefore(canvas, document.body.firstChild);
  }
  ctx = canvas.getContext('2d');
  if (!ctx) return; // very old/broken environment — fail silent, no backdrop, app unaffected

  reduceMotion = readReducedMotion();

  resizeAndRebuild();
  drawFrame(null, 0);

  window.addEventListener('resize', onResize);

  if (!reduceMotion) {
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  // Live theme-flip repaint (shell.js's theme toggle stamps data-theme on
  // <html>) — rebuild the cached lattice in the new tint and redraw once.
  const themeObserver = new MutationObserver(() => {
    resizeAndRebuild(); // cheap even when only the theme (not size) changed
    drawFrame(pointer, currentGlowStrength());
  });
  themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

  // The OS-level reduced-motion preference can change at runtime.
  if (window.matchMedia) {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onMqChange = (e) => {
      reduceMotion = e.matches;
      if (reduceMotion) {
        stopLoop();
        pointer = null;
        targetPointer = null;
        drawFrame(null, 0);
        window.removeEventListener('pointermove', onPointerMove);
        document.removeEventListener('visibilitychange', onVisibilityChange);
      } else {
        window.addEventListener('pointermove', onPointerMove, { passive: true });
        document.addEventListener('visibilitychange', onVisibilityChange);
      }
    };
    if (mq.addEventListener) mq.addEventListener('change', onMqChange);
    else if (mq.addListener) mq.addListener(onMqChange); // Safari <14 fallback
  }
}

function readReducedMotion() {
  return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

function isDarkTheme() {
  return document.documentElement.getAttribute('data-theme') !== 'light';
}

function currentGlowStrength() {
  if (reduceMotion || !pointer) return 0;
  const idleFor = Date.now() - lastMoveAt;
  return Math.max(0, 1 - idleFor / IDLE_FADE_MS);
}

function onResize() {
  resizeAndRebuild();
  drawFrame(pointer, currentGlowStrength());
}

function resizeAndRebuild() {
  dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
  w = window.innerWidth;
  h = window.innerHeight;
  canvas.width = Math.round(w * dpr);
  canvas.height = Math.round(h * dpr);
  canvas.style.width = `${w}px`;
  canvas.style.height = `${h}px`;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  rebuildBaseLattice();
}

/** Pre-renders the static hex lattice (base alpha, no glow) into an
 * offscreen canvas once per resize/theme change — every animated frame then
 * just blits this instead of re-stroking the whole grid. */
function rebuildBaseLattice() {
  if (!baseCanvas) baseCanvas = document.createElement('canvas');
  baseCanvas.width = canvas.width;
  baseCanvas.height = canvas.height;
  baseCtx = baseCanvas.getContext('2d');
  if (!baseCtx) return;
  baseCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
  baseCtx.clearRect(0, 0, w, h);

  const dark = isDarkTheme();
  const [lr, lg, lb] = dark ? DARK_LINE : LIGHT_LINE;
  const baseAlpha = dark ? DARK_BASE_ALPHA : LIGHT_BASE_ALPHA;

  const hexW = Math.sqrt(3) * HEX_R;
  const hexH = 2 * HEX_R;
  const vStep = hexH * 0.75;
  hexGeom = { hexW, hexH, vStep };

  const cols = Math.ceil(w / hexW) + 2;
  const rows = Math.ceil(h / vStep) + 2;

  baseCtx.lineWidth = 1;
  baseCtx.strokeStyle = `rgba(${lr},${lg},${lb},${baseAlpha})`;

  for (let row = -1; row <= rows; row++) {
    const cy = row * vStep;
    const rowOffset = row % 2 !== 0 ? hexW / 2 : 0;
    for (let col = -1; col <= cols; col++) {
      const cx = col * hexW + rowOffset;
      if (cx < -hexW || cx > w + hexW) continue;
      pathHex(baseCtx, cx, cy, HEX_R);
      baseCtx.stroke();
    }
  }
}

function pathHex(c, cx, cy, r) {
  c.beginPath();
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 180) * (60 * i - 30); // pointy-top
    const x = cx + r * Math.cos(angle);
    const y = cy + r * Math.sin(angle);
    if (i === 0) c.moveTo(x, y);
    else c.lineTo(x, y);
  }
  c.closePath();
}

function onVisibilityChange() {
  if (document.hidden) {
    stopLoop();
  } else if (currentGlowStrength() > 0) {
    startLoop();
  }
}

function onPointerMove(e) {
  targetPointer = { x: e.clientX, y: e.clientY };
  lastMoveAt = Date.now();
  if (!pointer) pointer = { ...targetPointer };
  startLoop();
}

function startLoop() {
  if (rafId != null || document.hidden || reduceMotion) return;
  rafId = requestAnimationFrame(tick);
}
function stopLoop() {
  if (rafId != null) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
}

function tick() {
  rafId = null;
  if (pointer && targetPointer) {
    pointer.x += (targetPointer.x - pointer.x) * POINTER_LERP;
    pointer.y += (targetPointer.y - pointer.y) * POINTER_LERP;
  }

  const glowStrength = currentGlowStrength();
  drawFrame(pointer, glowStrength);

  if (glowStrength > 0) {
    startLoop();
  }
  // else: fully idle-faded — loop stops here; the last-drawn (glow-free)
  // frame stays on screen until the next pointermove wakes it back up.
}

function drawFrame(activePointer, glowStrength) {
  if (!ctx || !baseCanvas) return;
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(baseCanvas, 0, 0, w, h);

  if (!activePointer || glowStrength <= 0.003 || !hexGeom) return;

  const dark = isDarkTheme();
  const [lr, lg, lb] = dark ? DARK_LINE : LIGHT_LINE;
  const glowAlpha = (dark ? DARK_GLOW_ALPHA : LIGHT_GLOW_ALPHA) * Math.min(Math.max(glowStrength, 0), 1);

  // Soft radial wash under the brightened hexes — the "glow" feel.
  const grad = ctx.createRadialGradient(
    activePointer.x, activePointer.y, 0,
    activePointer.x, activePointer.y, GLOW_RADIUS,
  );
  grad.addColorStop(0, `rgba(${lr},${lg},${lb},${(glowAlpha * 0.2).toFixed(3)})`);
  grad.addColorStop(1, `rgba(${lr},${lg},${lb},0)`);
  ctx.fillStyle = grad;
  ctx.fillRect(
    activePointer.x - GLOW_RADIUS, activePointer.y - GLOW_RADIUS,
    GLOW_RADIUS * 2, GLOW_RADIUS * 2,
  );

  // Brighten only the hexes within the glow radius — a small, distance-
  // bounded re-scan (~100-150 hexes), never the full lattice.
  const { hexW, vStep } = hexGeom;
  const colSpan = Math.ceil(GLOW_RADIUS / hexW) + 1;
  const rowSpan = Math.ceil(GLOW_RADIUS / vStep) + 1;
  const centerRow = Math.round(activePointer.y / vStep);
  const centerCol = Math.round(activePointer.x / hexW);

  ctx.lineWidth = 1;
  for (let row = centerRow - rowSpan; row <= centerRow + rowSpan; row++) {
    const cy = row * vStep;
    const rowOffset = row % 2 !== 0 ? hexW / 2 : 0;
    for (let col = centerCol - colSpan; col <= centerCol + colSpan; col++) {
      const cx = col * hexW + rowOffset;
      const dx = cx - activePointer.x;
      const dy = cy - activePointer.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist >= GLOW_RADIUS) continue;
      const alpha = glowAlpha * (1 - dist / GLOW_RADIUS);
      pathHex(ctx, cx, cy, HEX_R);
      ctx.strokeStyle = `rgba(${lr},${lg},${lb},${alpha.toFixed(3)})`;
      ctx.stroke();
    }
  }
}
